/**
 * @fileoverview Animations Public - アニメーション実行の公開インターフェース
 * gameplay層からgraphics層への直参照を避けるためのFacade層
 *
 * 仕組み：graphics層が初期化時に実装関数を登録し、gameplay層は登録された関数を呼び出す
 * これによりgameplay→graphicsの層断絶を維持しつつアニメーション実行が可能
 *
 * 設計方針：
 * - 最小限の間接層（存在チェック・Promiseラップなし）
 * - 初期化前の呼び出しはfail-fast（早期検出）
 * - 実装側の戻り値をそのまま返す（Promise管理は実装側の責務）
 */

/**
 * @typedef {Object} AnimationHandlers
 * @property {(gridSnapshot: Object, targetPieces: Array, initialYOffsets: Array) => Promise<void>} executeGravityAnimation - 重力アニメーション実行
 * @property {(removalPlan: Object) => Promise<void>} executeRemovalAnimation - 削除アニメーション実行
 * @property {(row: number, col: number, specialPiece: Object, matchedColors: Array) => Promise<void>} executeSpecialCreationAnimation - 特殊ピース生成アニメーション実行
 * @property {() => Promise<void>} resetBoard - ボードリセット
 * @property {(updates: Array<{row: number, col: number, piece: Object|null}>) => Promise<void>} syncBoardPieces - board pieces sync
 */

/** @type {AnimationHandlers} */
let _handlers;

/**
 * アニメーションハンドラーの登録（graphics層の初期化時に呼ばれる）
 * @param {AnimationHandlers} handlers - アニメーション実行関数群
 */
export function registerAnimationHandlers(handlers) {
  _handlers = handlers;
}

/**
 * 重力アニメーション実行
 * @param {Object} gridSnapshot - グリッドスナップショット
 * @param {Array} targetPieces - 対象ピース配列
 * @param {Array} initialYOffsets - 初期Yオフセット配列
 * @returns {Promise<void>}
 */
export function executeGravityAnimation(gridSnapshot, targetPieces, initialYOffsets) {
  return _handlers.executeGravityAnimation(gridSnapshot, targetPieces, initialYOffsets);
}

/**
 * 削除アニメーション実行
 * @param {Object} removalPlan - 削除プラン（transforms.pieceに完全なデータを含む）
 * @returns {Promise<void>}
 */
export function executeRemovalAnimation(removalPlan) {
  return _handlers.executeRemovalAnimation(removalPlan);
}

/**
 * 特殊ピース生成アニメーション実行
 * @param {number} row - 行
 * @param {number} col - 列
 * @param {Object} specialPiece - 特殊ピースデータ
 * @param {Array} matchedColors - マッチした色配列
 * @returns {Promise<void>}
 */
export function executeSpecialCreationAnimation(row, col, specialPiece, matchedColors) {
  return _handlers.executeSpecialCreationAnimation(row, col, specialPiece, matchedColors);
}

/**
 * ボードリセット
 * @returns {Promise<void>}
 */
export function resetBoard() {
  return _handlers.resetBoard();
}

/**
 * Board pieces sync facade
 * @param {Array<{row: number, col: number, piece: Object|null}>} updates - rendering payload
 * @returns {Promise<void>}
 */
export function syncBoardPieces(updates) {
  return _handlers.syncBoardPieces(updates);
}
